import { PlanPicker, type PlanCode } from '@/components/billing/PlanPicker';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordStrength } from '@/components/ui/PasswordStrength';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  COUNTRY_OPTIONS,
  UI_LOCALE_OPTIONS,
  defaultCurrencyForCountry,
  type CountryCode,
  type UiLocale,
} from '@/lib/locale';
import {
  detectDocumentKind,
  documentKindLabel,
  documentToApi,
  formatDocumentBR,
  formatPhoneBR,
  phoneToApi,
} from '@/lib/masks';
import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

const STEPS = [
  { id: 1, label: 'Conta' },
  { id: 2, label: 'Fazenda' },
  { id: 3, label: 'Senha' },
  { id: 4, label: 'Plano' },
] as const;

type FormState = {
  name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
  legal_name: string;
  document: string;
  ui_locale: UiLocale;
  country: CountryCode;
};

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function RegisterPage() {
  const { user, loading, register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<PlanCode>('trial');
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    legal_name: '',
    document: '',
    ui_locale: 'pt-BR',
    country: 'BR',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [startedSubmit, setStartedSubmit] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [mlConsent, setMlConsent] = useState(false);

  const docKind = detectDocumentKind(form.document);
  const currency = defaultCurrencyForCountry(form.country);
  const stepMeta = useMemo(() => STEPS.find((s) => s.id === step) ?? STEPS[0], [step]);

  // Já logado visitando /cadastro — não interferir no redirect pós-submit (trial/checkout).
  if (!loading && user && !startedSubmit) {
    return <Navigate to="/" replace />;
  }

  function update(field: keyof FormState, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
    setFieldErrors((e) => {
      const next = { ...e };
      delete next[field];
      return next;
    });
  }

  function validateStep(current: number): boolean {
    const errors: Record<string, string> = {};

    if (current === 1) {
      if (!form.name.trim()) errors.name = 'Informe seu nome.';
      if (!form.email.trim()) errors.email = 'Informe o e-mail.';
      else if (!isValidEmail(form.email)) errors.email = 'E-mail inválido.';
      if (!form.phone.trim() || phoneToApi(form.phone).length < 10) {
        errors.phone = 'Informe um telefone válido.';
      }
    }

    if (current === 2) {
      if (!form.legal_name.trim()) errors.legal_name = 'Informe a razão social ou fazenda.';
      const digits = documentToApi(form.document);
      if (!digits || (digits.length !== 11 && digits.length !== 14)) {
        errors.document = 'Informe um CPF ou CNPJ válido.';
      }
    }

    if (current === 3) {
      if (form.password.length < 8) errors.password = 'A senha deve ter pelo menos 8 caracteres.';
      if (form.password !== form.password_confirmation) {
        errors.password_confirmation = 'As senhas não coincidem.';
      }
    }

    if (current === 4 && !acceptTerms) {
      errors.accept_terms = 'Aceite os Termos de Uso e a Política de Privacidade para continuar.';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Revise os campos destacados para continuar.');
      return false;
    }
    setError('');
    return true;
  }

  function goNext() {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(4, s + 1));
  }

  function goBack() {
    setError('');
    setFieldErrors({});
    setStep((s) => Math.max(1, s - 1));
  }

  async function handleFinish() {
    if (!validateStep(3)) {
      setStep(3);
      return;
    }
    if (!validateStep(4)) {
      setStep(4);
      return;
    }
    setError('');
    setFieldErrors({});
    setStartedSubmit(true);
    setSubmitting(true);
    try {
      await register({
        ...form,
        phone: phoneToApi(form.phone),
        document: documentToApi(form.document),
        currency,
        accept_terms: true,
        ml_consent: mlConsent,
      });

      if (selectedPlan === 'trial') {
        navigate('/', { replace: true });
        return;
      }

      try {
        const res = await apiFetch<{ url: string }>('/billing/checkout', {
          method: 'POST',
          body: JSON.stringify({ plan_code: selectedPlan }),
        });
        window.location.href = res.url;
        return;
      } catch (checkoutErr) {
        navigate('/assinatura?checkout=cancel', { replace: true });
        setError(
          getErrorMessage(
            checkoutErr,
            'Conta criada. Não foi possível abrir o checkout agora — você pode assinar em Assinatura.',
          ),
        );
      }
    } catch (err) {
      setStartedSubmit(false);
      if (err instanceof ApiError) {
        if (err.fieldErrors) {
          const mapped: Record<string, string> = {};
          for (const [key, msgs] of Object.entries(err.fieldErrors)) {
            mapped[key] = msgs[0] ?? '';
          }
          setFieldErrors(mapped);
          if (mapped.name || mapped.email || mapped.phone) setStep(1);
          else if (mapped.legal_name || mapped.document || mapped.country || mapped.ui_locale) {
            setStep(2);
          } else if (mapped.password || mapped.password_confirmation) setStep(3);
          else if (mapped.accept_terms) setStep(4);
        }
        setError(err.message);
      } else {
        setError('Não foi possível criar a conta.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const titles: Record<number, { title: string; subtitle: string }> = {
    1: {
      title: 'Crie sua conta',
      subtitle: 'Dados de acesso para começar no Manejai',
    },
    2: {
      title: 'Sua fazenda',
      subtitle: 'Usamos isso na fatura e nos formatos regionais',
    },
    3: {
      title: 'Defina sua senha',
      subtitle: 'Escolha uma senha forte para proteger a conta',
    },
    4: {
      title: 'Escolha seu plano',
      subtitle: 'Comece com 30 dias grátis ou assine agora',
    },
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="relative hidden lg:flex lg:w-1/2 flex-col justify-between overflow-hidden p-12 text-white"
        style={{
          backgroundImage: "url('/images/login-hero.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-manejai-forest/85 via-manejai-forest/55 to-manejai-moss/70" />
        <div className="relative z-10">
          <p className="font-display text-3xl font-semibold">Manejai</p>
          <p className="mt-2 text-manejai-cream/90 text-lg">Gestão de rebanho com clareza</p>
        </div>
        <div className="relative z-10 space-y-4 max-w-md">
          <p className="text-manejai-cream/90 text-sm leading-relaxed">
            Em poucos minutos você cria a conta, configura a fazenda e escolhe o plano — com trial
            de 30 dias para testar no campo.
          </p>
          <div className="flex flex-wrap gap-2 text-xs text-manejai-cream/80">
            {STEPS.map((s) => (
              <span
                key={s.id}
                className={cn(
                  'rounded-full px-3 py-1 backdrop-blur-sm',
                  step === s.id ? 'bg-white/25' : 'bg-white/10',
                )}
              >
                {s.id}. {s.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-6 lg:hidden">
            <p className="font-display text-2xl font-semibold text-manejai-forest">Manejai</p>
            <p className="mt-1 text-sm text-manejai-bark/70">Criar conta</p>
          </div>

          <nav aria-label="Progresso do cadastro" className="mb-6">
            <ol className="flex items-center gap-2">
              {STEPS.map((s, index) => {
                const done = s.id < step;
                const active = s.id === step;
                return (
                  <li key={s.id} className="flex flex-1 items-center gap-2">
                    <div
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                        done || active
                          ? 'bg-manejai-forest text-white'
                          : 'bg-manejai-sand/70 text-manejai-bark/50',
                      )}
                      aria-current={active ? 'step' : undefined}
                    >
                      {s.id}
                    </div>
                    <span
                      className={cn(
                        'hidden text-xs font-medium sm:inline',
                        active ? 'text-manejai-forest' : 'text-manejai-bark/45',
                      )}
                    >
                      {s.label}
                    </span>
                    {index < STEPS.length - 1 && (
                      <div
                        className={cn(
                          'h-px flex-1',
                          done ? 'bg-manejai-forest/40' : 'bg-manejai-sand',
                        )}
                      />
                    )}
                  </li>
                );
              })}
            </ol>
            <p className="mt-2 text-xs text-manejai-bark/50 sm:hidden">
              Passo {step} de {STEPS.length}: {stepMeta.label}
            </p>
          </nav>

          <h1 className="font-display text-2xl font-semibold text-manejai-forest">
            {titles[step].title}
          </h1>
          <p className="mt-1 mb-6 text-sm text-manejai-bark/70">{titles[step].subtitle}</p>

          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          <div className="space-y-4">
            {step === 1 && (
              <>
                <Input
                  label="Seu nome completo"
                  required
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  error={fieldErrors.name}
                />
                <Input
                  label="E-mail"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  error={fieldErrors.email}
                />
                <Input
                  label="Telefone"
                  required
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(11) 99999-9999"
                  value={form.phone}
                  onChange={(e) => update('phone', formatPhoneBR(e.target.value))}
                  error={fieldErrors.phone}
                />
              </>
            )}

            {step === 2 && (
              <>
                <Input
                  label="Razão social / Fazenda"
                  required
                  value={form.legal_name}
                  onChange={(e) => update('legal_name', e.target.value)}
                  error={fieldErrors.legal_name}
                />
                <Input
                  label={
                    docKind !== 'unknown'
                      ? `CPF/CNPJ (${documentKindLabel(docKind)})`
                      : 'CPF/CNPJ'
                  }
                  required
                  value={form.document}
                  onChange={(e) => update('document', formatDocumentBR(e.target.value))}
                  error={fieldErrors.document}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Idioma da interface"
                    value={form.ui_locale}
                    onChange={(e) => update('ui_locale', e.target.value)}
                    options={UI_LOCALE_OPTIONS}
                    error={fieldErrors.ui_locale}
                  />
                  <Select
                    label="País / região"
                    value={form.country}
                    onChange={(e) => update('country', e.target.value)}
                    options={COUNTRY_OPTIONS.map((c) => ({ value: c.value, label: c.label }))}
                    error={fieldErrors.country}
                  />
                </div>
                <p className="text-xs text-manejai-bark/55">
                  O país define formatos de data, número e moeda ({currency}).
                </p>
              </>
            )}

            {step === 3 && (
              <>
                <div className="space-y-3">
                  <Input
                    label="Senha"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => update('password', e.target.value)}
                    error={fieldErrors.password}
                  />
                  <PasswordStrength password={form.password} />
                </div>
                <Input
                  label="Confirmar senha"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={form.password_confirmation}
                  onChange={(e) => update('password_confirmation', e.target.value)}
                  error={fieldErrors.password_confirmation}
                />
              </>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <PlanPicker value={selectedPlan} onChange={setSelectedPlan} />
                <div className="space-y-3 rounded-xl border border-manejai-sand/80 bg-manejai-cream/30 p-4">
                  <label className="flex gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-manejai-sand text-manejai-forest focus:ring-manejai-moss"
                      checked={acceptTerms}
                      onChange={(e) => {
                        setAcceptTerms(e.target.checked);
                        if (e.target.checked) {
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.accept_terms;
                            return next;
                          });
                        }
                      }}
                    />
                    <span className="text-sm text-manejai-bark/85 leading-relaxed">
                      Li e aceito os{' '}
                      <Link
                        to="/termos"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-manejai-moss hover:underline"
                      >
                        Termos de Uso
                      </Link>{' '}
                      e a{' '}
                      <Link
                        to="/privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-manejai-moss hover:underline"
                      >
                        Política de Privacidade
                      </Link>
                      .
                    </span>
                  </label>
                  {fieldErrors.accept_terms && (
                    <p className="text-sm text-red-600">{fieldErrors.accept_terms}</p>
                  )}
                  <label className="flex gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-manejai-sand text-manejai-forest focus:ring-manejai-moss"
                      checked={mlConsent}
                      onChange={(e) => setMlConsent(e.target.checked)}
                    />
                    <span className="text-sm text-manejai-bark/75 leading-relaxed">
                      Autorizo o uso de fotos e vídeos capturados na plataforma para melhorar a
                      identificação por imagem (opcional — pode alterar depois em Configurações).
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={goBack}
                  disabled={submitting}
                >
                  Voltar
                </Button>
              )}

              {step < 4 ? (
                <Button type="button" className="flex-1" size="lg" onClick={goNext}>
                  Continuar
                </Button>
              ) : (
                <Button
                  type="button"
                  className="flex-1"
                  size="lg"
                  loading={submitting}
                  onClick={() => void handleFinish()}
                >
                  {selectedPlan === 'trial' ? 'Começar grátis' : 'Criar conta e assinar'}
                </Button>
              )}
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-manejai-bark/70">
            Já tem conta?{' '}
            <Link to="/login" className="font-medium text-manejai-moss hover:underline">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
